import { computeGateMetrics, grayFromRgba, luma } from './gates';
import { AreaReducer, gateRasterSize } from './raster';
import type { GateMetrics } from '../fsm/machine';

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
// LA FOTOCAMERA (ARCHITETTURA §7.2): posteriore, 1600×1200 chiesto (la EDA61K tenuta in verticale restituisce 1200×1600:
// misurato il 13/10/2026), fuoco manuale a ~3 m dove c'è, un solo percorso di
// scatto in v1 (canvas dal video: una calibrazione sola). Regola di memoria: mai più di un fotogramma pieno vivo.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

/** La distanza a cui si blocca il fuoco, in metri (quella di lavoro è ~2,5 m; ~3 m è l'intento del progetto). */
export const FOCUS_TARGET_M = 2.9;

export interface CameraHandle {
  stream: MediaStream;
  track: MediaStreamTrack;
  settings: MediaTrackSettings;
  /** Esito del blocco del fuoco: 'locked', 'unsupported' (il dispositivo non ha il fuoco manuale) o il motivo del rifiuto. */
  focus: string;
  stop(): void;
}

/**
 * La distanza di fuoco da chiedere, o null se il fuoco manuale non c'è. Chrome rifiuta un valore fuori dall'intervallo che il
 * dispositivo dichiara ("focusDistance setting out of range"): sulla EDA61K va da 0,10 a 2,95 m, e il 3 che si chiedeva prima
 * non veniva mai applicato (l'errore era ignorato in silenzio e il fuoco restava continuo).
 */
export function focusDistanceToApply(caps: Record<string, unknown>, target = FOCUS_TARGET_M): number | null {
  const modes = Array.isArray(caps.focusMode) ? (caps.focusMode as string[]) : [];
  const range = caps.focusDistance;
  if (!modes.includes('manual') || typeof range !== 'object' || range === null) return null;
  const { min, max } = range as { min?: number; max?: number };
  return Math.min(Math.max(target, min ?? -Infinity), max ?? Infinity);
}

export async function openRearCamera(): Promise<CameraHandle> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: false,
    video: { facingMode: { ideal: 'environment' }, width: { ideal: 1600 }, height: { ideal: 1200 }, frameRate: { ideal: 15, max: 30 } },
  });
  const track = stream.getVideoTracks()[0];
  if (!track) {
    stream.getTracks().forEach((t) => t.stop());
    throw new Error('Nessuna traccia video');
  }

  // Fuoco manuale a ~3 m dove il browser lo espone (Chrome Android): un fuoco che caccia fra un fotogramma e l'altro
  // cambia la focale effettiva e la calibrazione con lei (A §4.4). Se non c'è, resta continuo e `focus` lo dice.
  const caps = (track.getCapabilities?.() ?? {}) as Record<string, unknown>;
  const distance = focusDistanceToApply(caps);
  let focus = 'unsupported';
  if (distance !== null) {
    try {
      await track.applyConstraints({ advanced: [{ focusMode: 'manual', focusDistance: distance } as MediaTrackConstraintSet] });
      // Si controlla ciò che il dispositivo ha davvero fatto: un vincolo "advanced" può essere ignorato senza errore.
      focus = (track.getSettings() as { focusMode?: string }).focusMode === 'manual' ? 'locked' : 'ignored';
    } catch (e) {
      focus = e instanceof Error ? e.message : String(e);
    }
  }

  return { stream, track, settings: track.getSettings(), focus, stop: () => stream.getTracks().forEach((t) => t.stop()) };
}

/**
 * Il raster dei gate dal video, alla forma del fotogramma (ADR-029): economico, si può fare 5 volte al secondo. Qui la riduzione
 * la fa `drawImage` (dipende dal browser): basta per il richiamo dal vivo. Il numero che finisce nel caso registrato e che il
 * server ricontrolla è quello di `captureJpeg`, con la media su area di `raster.ts`.
 */
export function gateRasterFrom(source: CanvasImageSource, sourceWidthPx: number, sourceHeightPx: number, scratch: HTMLCanvasElement): { gray: Uint8Array; widthPx: number; heightPx: number } {
  const { widthPx, heightPx } = gateRasterSize(sourceWidthPx, sourceHeightPx);
  scratch.width = widthPx;
  scratch.height = heightPx;
  const ctx = scratch.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(source, 0, 0, widthPx, heightPx);
  return { gray: grayFromRgba(ctx.getImageData(0, 0, widthPx, heightPx).data, widthPx, heightPx), widthPx, heightPx };
}

export function liveGates(video: HTMLVideoElement, scratch: HTMLCanvasElement): GateMetrics {
  const r = gateRasterFrom(video, video.videoWidth, video.videoHeight, scratch);
  return computeGateMetrics(r.gray, r.widthPx, r.heightPx);
}

/** Le metriche esatte di un fotogramma già su canvas: media su area, a fasce di righe (mai due fotogrammi pieni in memoria). */
export function exactGates(canvas: HTMLCanvasElement): GateMetrics {
  const { width, height } = canvas;
  const target = gateRasterSize(width, height);
  const reducer = new AreaReducer(width, height, target);
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  const band = 64;
  const row = new Float64Array(width);
  for (let y0 = 0; y0 < height; y0 += band) {
    const h = Math.min(band, height - y0);
    const rgba = ctx.getImageData(0, y0, width, h).data;
    for (let r = 0; r < h; r++) {
      for (let x = 0, j = r * width * 4; x < width; x++, j += 4) row[x] = luma(rgba[j]!, rgba[j + 1]!, rgba[j + 2]!);
      reducer.addRow(row);
    }
  }
  return computeGateMetrics(reducer.result(), target.widthPx, target.heightPx);
}

export interface CapturedJpeg {
  blob: Blob;
  widthPx: number;
  heightPx: number;
  gates: GateMetrics;
}

/**
 * Lo scatto: canvas alla risoluzione nativa del video, JPEG q85. `toBlob` con un tipo non supportato restituisce PNG in
 * silenzio (B R3): si verifica il tipo. I gate si ricalcolano sullo stesso canvas, così server e client giudicano gli
 * stessi pixel.
 */
export async function captureJpeg(video: HTMLVideoElement, quality = 0.85): Promise<CapturedJpeg> {
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  // `willReadFrequently`: la lettura a fasce di `exactGates` sul canvas con il fotogramma (Chrome avvisa se manca).
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(video, 0, 0);
  const gates = exactGates(canvas);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  canvas.width = canvas.height = 0; // libera subito i 7,7 MB del raster
  if (!blob || blob.type !== 'image/jpeg') {
    throw new Error(`Il browser non produce JPEG (tipo: ${blob?.type ?? 'nessuno'})`);
  }
  return { blob, widthPx: video.videoWidth, heightPx: video.videoHeight, gates };
}

interface ImageCaptureLike {
  getPhotoCapabilities(): Promise<{ imageWidth?: { max?: number }; imageHeight?: { max?: number } }>;
  takePhoto(settings?: Record<string, unknown>): Promise<Blob>;
}

/**
 * La foto ferma a piena risoluzione dalla stessa traccia (Image Capture API), oppure null se il browser non la offre. È la foto vera della
 * fotocamera, non un fotogramma del video: più pixel e, se ha la stessa inquadratura dell'app fotocamera, la stessa calibrazione. Il lampo resta
 * spento: un riflesso sul film cambierebbe il contorno.
 */
export async function takeStill(track: MediaStreamTrack): Promise<Blob | null> {
  const Ctor = (globalThis as { ImageCapture?: new (t: MediaStreamTrack) => ImageCaptureLike }).ImageCapture;
  if (!Ctor) return null;
  const capture = new Ctor(track);
  const caps = await capture.getPhotoCapabilities().catch(() => null);
  const w = caps?.imageWidth?.max;
  const h = caps?.imageHeight?.max;
  return capture.takePhoto(w && h ? { imageWidth: w, imageHeight: h, fillLightMode: 'off' } : { fillLightMode: 'off' });
}

/** Un callback per fotogramma, limitato a maxFps: con requestVideoFrameCallback dove c'è, altrimenti un timer. */
export function startFrameLoop(video: HTMLVideoElement, onFrame: (nowMs: number) => void, maxFps = 5): () => void {
  const minInterval = 1000 / maxFps;
  let last = 0;
  let stopped = false;
  const rvfc = (video as HTMLVideoElement & { requestVideoFrameCallback?: (cb: (now: number) => void) => number }).requestVideoFrameCallback;
  if (typeof rvfc === 'function') {
    const tick = (now: number): void => {
      if (stopped) return;
      if (now - last >= minInterval) {
        last = now;
        onFrame(now);
      }
      rvfc.call(video, tick);
    };
    rvfc.call(video, tick);
    return () => {
      stopped = true;
    };
  }
  const id = setInterval(() => onFrame(performance.now()), minInterval);
  return () => clearInterval(id);
}

/** Schermo acceso durante la cattura; se l'API manca o è negata, si va avanti senza. */
export async function requestWakeLock(): Promise<() => void> {
  try {
    const lock = await navigator.wakeLock?.request('screen');
    return () => void lock?.release();
  } catch {
    return () => undefined;
  }
}

/** I valori "ad alta entropia" delle User-Agent Client Hints: l'unico posto dove Chrome dichiara ancora il modello vero. */
export interface UaHighEntropy {
  model?: string;
  platformVersion?: string;
  uaFullVersion?: string;
}

/**
 * Modello e impronta dal dispositivo. Da Chrome 110 la stringa `navigator.userAgent` è congelata e MENTE: sulla EDA61K
 * (Android 9, Chrome 138) dice «Android 10; K», cioè nessun modello e una versione di Android falsa, uguale per tutte le pistole.
 * Le Client Hints dicono il vero («EDA61K», «9.0.0», «138.0.7204.179», verificato sul dispositivo il 13/10/2026): se ci sono si usano
 * quelle, altrimenti si ripiega sulla stringa classica (il modello «K» non è un modello e diventa «unknown»).
 */
export function fingerprintFrom(ua: string, hints: UaHighEntropy | null): { fingerprint: string; model: string } {
  const hinted = hints?.model?.trim();
  if (hinted) {
    return { fingerprint: `${hinted};Android ${hints?.platformVersion ?? '?'};Chrome ${hints?.uaFullVersion ?? '?'}`, model: hinted };
  }
  const fromUa = /EDA61K/i.test(ua) ? 'EDA61K' : /\(Linux; Android [^;]+; ([^)]+)\)/.exec(ua)?.[1];
  return { fingerprint: ua, model: fromUa && fromUa !== 'K' ? fromUa : 'unknown' };
}

/** L'impronta del dispositivo, la chiave del profilo di calibrazione (§6.6). Asincrona: le Client Hints si chiedono al browser. */
export async function deviceFingerprint(): Promise<{ fingerprint: string; model: string }> {
  const uaData = (navigator as Navigator & { userAgentData?: { getHighEntropyValues(hints: string[]): Promise<UaHighEntropy> } }).userAgentData;
  let hints: UaHighEntropy | null = null;
  try {
    hints = (await uaData?.getHighEntropyValues(['model', 'platformVersion', 'uaFullVersion'])) ?? null;
  } catch {
    hints = null; // fuori da un contesto sicuro, o rifiutate: si ripiega sulla stringa classica
  }
  return fingerprintFrom(navigator.userAgent, hints);
}
